import { cn } from '@/utils/cn';
import type { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/Skeleton';
import { getKpiIconClassName } from '@/config/appAccent';

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: ReactNode;
  /** Rotating tone index — set automatically when used inside KpiGrid. */
  iconToneIndex?: number;
  trend?: string;
  trendDirection?: 'up' | 'down' | 'neutral';
  loading?: boolean;
  className?: string;
}

const trendColors = {
  up: 'text-success-700 dark:text-emerald-400',
  down: 'text-error-700 dark:text-red-400',
  neutral: 'text-slate-500 dark:text-slate-400',
};

export function StatCard({
  title,
  value,
  icon,
  iconToneIndex = 0,
  trend,
  trendDirection = 'neutral',
  loading,
  className,
}: StatCardProps) {
  return (
    <div className={cn('card p-5 transition-colors', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{title}</p>
          {loading ? (
            <Skeleton className="mt-2 h-8 w-24" />
          ) : (
            <p className="mt-1.5 text-2xl font-semibold tabular-nums tracking-tight text-slate-900 dark:text-white">
              {value}
            </p>
          )}
          {trend && !loading && (
            <p className={cn('mt-1 text-xs font-medium', trendColors[trendDirection])}>{trend}</p>
          )}
        </div>
        {icon && (
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg [&_.MuiSvgIcon-root]:text-[1.25rem]',
              getKpiIconClassName(iconToneIndex),
            )}
          >
            {icon}
          </div>
        )}
      </div>
    </div>
  );
}

export function StatCardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('card p-5', className)}>
      <Skeleton className="h-4 w-24" />
      <Skeleton className="mt-3 h-8 w-20" />
    </div>
  );
}
